import { ChevronDownIcon, ChevronUpIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import React from 'react';

interface SearchStripProps {
    searchQuery: string;
    onSearchChange: (query: string) => void;
    activeMatchIdx: number;
    flatMatchesCount: number;
    onPrevMatch: () => void;
    onNextMatch: () => void;
    onClose: () => void;
    inputRef: React.Ref<HTMLInputElement>;
}

const SearchStrip: React.FC<SearchStripProps> = ({
    searchQuery,
    onSearchChange,
    activeMatchIdx,
    flatMatchesCount,
    onPrevMatch,
    onNextMatch,
    onClose,
    inputRef
}) => {
    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            if (e.shiftKey) onPrevMatch();
            else onNextMatch();
        }
        if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
        }
    };

    return (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 bg-white flex-shrink-0">
            <div className="relative flex-1">
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-500 absolute left-2 top-1/2 -translate-y-1/2" />
                <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="Search"
                    aria-label="Search in chat"
                    className="pl-7 pr-2 py-1 text-xs rounded-md border border-gray-300 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 w-full"
                />
            </div>
            <div className="text-[11px] text-gray-600 min-w-[44px] text-center">
                {activeMatchIdx >= 0 && flatMatchesCount > 0
                    ? `${activeMatchIdx + 1}/${flatMatchesCount}`
                    : `0/${flatMatchesCount}`}
            </div>
            <div className="flex items-center">
                <button
                    type="button"
                    onClick={onPrevMatch}
                    disabled={flatMatchesCount === 0}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                    aria-label="Previous match"
                >
                    <ChevronUpIcon className="w-4 h-4 text-gray-600" />
                </button>
                <button
                    type="button"
                    onClick={onNextMatch}
                    disabled={flatMatchesCount === 0}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                    aria-label="Next match"
                >
                    <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                </button>
            </div>
            <button
                type="button"
                onClick={onClose}
                className="text-blue-600 text-xs font-semibold px-2 py-1 hover:bg-blue-50 rounded"
            >
                Done
            </button>
        </div>
    );
};

export { SearchStrip };
